import { createSlice } from "@reduxjs/toolkit";

import { hasPeople, isInRoom, isLive, participantFor, PHASES, sameId } from "./callsModel.mjs";

export const initialSession = () => ({
  phase: PHASES.idle,
  callId: null,
  threadId: null,
  name: "",
  minimized: false,
  busy: false,
  message: "",
  // In-call banner, e.g. "<host> left the call"; the call view stays open.
  notice: "",
  error: null,
  mode: null,
});
const initialState = () => ({
  byId: {},
  activeByThread: {},
  incomingIds: [],
  restoreIds: [],
  session: initialSession(),
  // Popup after someone else ended the call for everyone; outlives the cleared session.
  endedNotice: null,
});

const callsSlice = createSlice({
  name: "calls",
  initialState: initialState(),
  reducers: {
    received(state, { payload: { call, userId, myState } }) {
      if (!call?.id || !call.thread_id) return;
      // An ended snapshot is a tombstone: delayed responses cannot resurrect it.
      if (state.byId[call.id]?.status === "ended" && call.status !== "ended") return;
      state.byId[call.id] = call;
      if (isLive(call)) state.activeByThread[call.thread_id] = call.id;
      else if (sameId(state.activeByThread[call.thread_id], call.id))
        delete state.activeByThread[call.thread_id];
      const ownState = myState || participantFor(call, userId)?.state;
      const ringing = isLive(call) && ownState === "ringing";
      if (!ringing) state.incomingIds = state.incomingIds.filter((id) => !sameId(id, call.id));
      else if (!state.incomingIds.some((id) => sameId(id, call.id)))
        state.incomingIds.push(call.id);
      // A dropped user can only rejoin while someone is still in the room.
      const restorable = isInRoom(ownState) || (ownState === "left" && hasPeople(call));
      if (!isLive(call) || !restorable) {
        state.restoreIds = state.restoreIds.filter((id) => !sameId(id, call.id));
      } else if (!state.restoreIds.includes(call.id)) {
        state.restoreIds.push(call.id);
      }
    },
    dismissInvitation(state, { payload: id }) {
      state.incomingIds = state.incomingIds.filter((item) => !sameId(item, id));
    },
    unavailable(state, { payload: id }) {
      const call = state.byId[id];
      if (call) {
        call.unavailable = true;
        if (sameId(state.activeByThread[call.thread_id], id))
          delete state.activeByThread[call.thread_id];
      }
      state.incomingIds = state.incomingIds.filter((item) => !sameId(item, id));
      state.restoreIds = state.restoreIds.filter((item) => !sameId(item, id));
    },
    restored(state, { payload: ids }) {
      state.restoreIds = ids.filter((id) => isLive(state.byId[id]));
    },
    sessionPatched(state, { payload }) {
      Object.assign(state.session, payload);
    },
    sessionCleared(state) {
      state.session = initialSession();
    },
    endedNoticeShown(state, { payload }) {
      state.endedNotice = payload;
    },
    endedNoticeDismissed(state) {
      state.endedNotice = null;
    },
    reset: () => initialState(),
  },
});

export const callsActions = callsSlice.actions;
export default callsSlice.reducer;
